import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Text,
  useDisclosure,
  Icon,
  Portal,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { useStores } from "../store/store_context";
import { useEffect, useState, useRef } from "react";
import { FiChevronDown, FiChevronUp, FiSearch, FiX } from "react-icons/fi";

// Выбор контрагентов списком с поиском — так же устроен, как выбор менеджеров
// (ModalSelectManagers): отметить несколько, «Выбрать все», «Сбросить»,
// «Применить». Список — pageStore.all_agents, выбор — pageStore.selected_agents.
const ModalSelectPartners = observer(() => {
  const { pageStore } = useStores();
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const partners = Array.from(
    new Set((pageStore.all_agents || []).map((item) => item?.Partner).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, "ru"));
  const needle = search.trim().toLowerCase();
  const filtered = needle ? partners.filter((name) => name.toLowerCase().includes(needle)) : partners;

  useEffect(() => {
    setSelected(pageStore.selected_agents ?? []);
  }, [pageStore.selected_agents]);

  // Клик мимо окна — применить выбор и закрыть, как у менеджеров.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!isOpen) return;
      const path = e.composedPath();
      if (!path.includes(dropdownRef.current) && !path.includes(menuRef.current)) {
        pageStore.updateSelectedAgents(selected);
        onClose();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen, selected, onClose, pageStore]);

  const toggle = (name) =>
    setSelected((prev) => (prev.includes(name) ? prev.filter((v) => v !== name) : [...prev, name]));

  const allFilteredSelected = filtered.length > 0 && filtered.every((name) => selected.includes(name));

  const handleSelectAll = () => {
    if (allFilteredSelected) setSelected((prev) => prev.filter((name) => !filtered.includes(name)));
    else setSelected((prev) => Array.from(new Set([...prev, ...filtered])));
  };

  const handleSave = () => {
    pageStore.updateSelectedAgents(selected);
    onClose();
  };

  const handleReset = () => {
    setSelected([]);
    setSearch("");
    pageStore.updateSelectedAgents([]);
  };

  const onToggleDropdown = () => {
    if (!isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    }
    onToggle();
  };

  return (
    <Box position="relative" width="200px" ref={dropdownRef}>
      <Button
        w="100%"
        borderRadius="10px"
        bg="rgb(51, 51, 51)"
        color="myColor.white"
        fontSize={"12px"}
        _hover={{ bg: "rgb(27, 26, 26)", color: "myColor.green" }}
        rightIcon={<Icon as={isOpen ? FiChevronUp : FiChevronDown} />}
        onClick={onToggleDropdown}
        justifyContent="space-between"
      >
        {selected.length > 0 ? `Контрагентов: ${selected.length}` : "Контрагенты"}
      </Button>
      <Portal>
        <Collapse in={isOpen} animateOpacity>
          <Box
            ref={menuRef}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            mt={2}
            p={4}
            bg="#1e1e1e"
            borderRadius="md"
            boxShadow="lg"
            zIndex="dropdown"
            position="absolute"
            top={`${dropdownPos.top}px`}
            left={`${dropdownPos.left}px`}
            width="600px"
          >
            <InputGroup mb={3}>
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Поиск контрагентов"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                color="myColor.white"
                _placeholder={{ color: "gray.400" }}
              />
            </InputGroup>

            <Box maxH="600px" overflowY="auto" mb={3}>
              <List spacing={2}>
                <ListItem p={1} borderRadius="md" _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}>
                  <Checkbox
                    isChecked={allFilteredSelected}
                    isIndeterminate={!allFilteredSelected && filtered.some((name) => selected.includes(name))}
                    onChange={handleSelectAll}
                    colorScheme="blue"
                    color="myColor.white"
                  >
                    <Text as="span" fontWeight="bold">Выбрать все</Text>
                  </Checkbox>
                </ListItem>
                {filtered.map((name) => (
                  <ListItem key={name} p={1}>
                    <Checkbox
                      isChecked={selected.includes(name)}
                      onChange={() => toggle(name)}
                      colorScheme="blue"
                      color="myColor.white"
                    >
                      {name}
                    </Checkbox>
                  </ListItem>
                ))}
              </List>
            </Box>
            <Flex justify="space-between">
              <Button leftIcon={<Icon as={FiX} />} variant="ghost" color="gray.400" onClick={handleReset}>
                Сбросить
              </Button>
              <Button
                bg="rgba(66, 62, 62, 1)"
                color="myColor.white"
                _hover={{ bg: "rgba(106, 99, 99, 1)", color: "myColor.green" }}
                onClick={handleSave}
              >
                Применить
              </Button>
            </Flex>
          </Box>
        </Collapse>
      </Portal>
    </Box>
  );
});

export default ModalSelectPartners;
